/**
 * Indicators/Oscillators cluster — cookbook seed recipes.
 *
 * Each entry validates against the real buildGraphFromRecipe engine.
 * Run the companion test to confirm every recipe builds with zero errors.
 *
 * Node-type and handle references verified against:
 *   apps/frontend/src/features/backtest/schemas/node-schemas/
 */

import type { CookbookEntry } from './types'

// ── 1. RSI Threshold ─────────────────────────────────────────
//
// Pattern: fire entry when RSI crosses into oversold territory (< 30),
// suggesting a mean-reversion long opportunity.
//
// Nodes: priceData → relativestrengthindex(14) → condition(rsi < 30 static) → Trade Manager
const rsiThreshold: CookbookEntry = {
  key: 'rsi-threshold',
  name: 'RSI Threshold (Oversold Entry)',
  category: 'indicators',
  intent: 'Enter long when RSI dips below 30, signalling an oversold mean-reversion setup.',
  summary: 'Enter long when RSI drops below 30, flagging an oversold mean-reversion opportunity.',
  difficulty: 'beginner',
  tags: ['Mean reversion', 'RSI'],
  whenToUse:
    'Best in range-bound or mildly trending markets where price tends to oscillate rather than trend relentlessly. Avoid in strong downtrends where RSI can stay oversold for extended periods.',
  whatToLookFor:
    'RSI crossing below 30 on a daily bar. Strongest when price is near a known support level and volume confirms the potential reversal rather than suggesting continued selling.',
  howItWorks:
    'A priceData node supplies daily closes to a relativestrengthindex(14) node, which outputs a 0–100 momentum reading. A condition node tests whether that RSI value is below the static literal 30 — the conventional oversold threshold. When the condition fires true, the Trade Manager places a Long order. The chain is deliberately minimal: one indicator, one threshold, one direction.',
  recipe: [
    {
      id: 'price',
      type: 'priceData',
      params: { timeframe: '1D' },
    },
    {
      // RSI takes a single numeric field — wire price.close
      id: 'rsi',
      type: 'relativestrengthindex',
      params: { period: 14 },
      inputs: { input_1: 'price.close' },
    },
    {
      // Static literal 30 as input_2 — discriminated union supports it
      id: 'oversold',
      type: 'condition',
      params: { operator: '<' },
      inputs: { input_1: 'rsi.rsi', input_2: 30 },
    },
  ],
  tradeManager: {
    params: { orderDirection: 'Long' },
    feeds: { socket: 'trigger', from: 'oversold.true' },
  },
}

// ── 2. RSI Pullback Reset ─────────────────────────────────────
//
// Pattern: uptrend confirmed by price above a 50-period SMA; within that
// uptrend, RSI cooled below 50 and is now recovering above it. Both
// conditions must hold simultaneously.
//
// Nodes:
//   priceData → simplemovingaverage(50)  → condition(close > sma)  ─┐
//   priceData → relativestrengthindex(14) → condition(rsi > 50)    ─┼→ merge(AND) → Trade Manager
const rsiPullbackReset: CookbookEntry = {
  key: 'rsi-pullback-reset',
  name: 'RSI Pullback Reset in Uptrend',
  category: 'indicators',
  intent:
    'Enter long when an uptrend (close > SMA-50) holds and RSI recovers back above 50 after cooling.',
  summary:
    'Buy the pullback in an established uptrend: price above SMA-50 AND RSI recovering back above 50.',
  difficulty: 'intermediate',
  tags: ['Pullback', 'RSI', 'Moving average'],
  whenToUse:
    'Trending markets where price has pulled back and momentum is recovering — the swing-trading "dip within strength" setup. Less effective in choppy, range-bound conditions where SMA alignment is unreliable.',
  whatToLookFor:
    'Price holding above the 50-period SMA while RSI crosses back over 50. Confirmation is stronger if the RSI dip stayed above 40 (healthy pullback) rather than washing out into oversold territory.',
  howItWorks:
    'Two independent condition checks feed a single AND gate. The first: priceData.close is compared against a simplemovingaverage(50) output to confirm the long-term uptrend is intact. The second: relativestrengthindex(14) is checked for a reading above 50, confirming momentum has reset and is turning back up. Both condition nodes emit their .true output into a merge(AND) node — entry fires only when both agree simultaneously.',
  recipe: [
    {
      id: 'price',
      type: 'priceData',
      params: { timeframe: '1D' },
    },
    {
      id: 'trend_ma',
      type: 'simplemovingaverage',
      params: { period: 50 },
      inputs: { input_1: 'price.close' },
    },
    {
      // Uptrend filter: close above the 50-period SMA
      id: 'above_ma',
      type: 'condition',
      params: { operator: '>' },
      inputs: { input_1: 'price.close', input_2: 'trend_ma.sma' },
    },
    {
      id: 'rsi',
      type: 'relativestrengthindex',
      params: { period: 14 },
      inputs: { input_1: 'price.close' },
    },
    {
      // RSI recovery above 50 signals the pullback has reset
      id: 'rsi_reset',
      type: 'condition',
      params: { operator: '>' },
      inputs: { input_1: 'rsi.rsi', input_2: 50 },
    },
    {
      // Both conditions must be true: uptrend AND RSI reset
      id: 'gate',
      type: 'merge',
      params: { operator: 'AND' },
      inputs: { input_1: 'above_ma.true', input_2: 'rsi_reset.true' },
    },
  ],
  tradeManager: {
    params: { orderDirection: 'Long' },
    feeds: { socket: 'trigger', from: 'gate.result' },
  },
}

// ── Export ───────────────────────────────────────────────────

export const indicatorsSeedRecipes: CookbookEntry[] = [rsiThreshold, rsiPullbackReset]
